import {
  FormSchemaValidator,
  FormSchemaConverter,
  IFileInputElement,
  RepeatableMetaInfo,
  isFileInputElement,
  isInputElement,
  isStaticElement,
} from "@graffer-inc/form-schema";

const ValidationConstant = {
  TOTAL_MAX_SECTION_COUNT: 20,
  TOTAL_MAX_INPUT_ELEMENTS: 400,
  TOTAL_MAX_FILE_SIZE: 50,
};

type FlattenElementsCache = ReturnType<
  typeof FormSchemaConverter.prototype.expand
>;

export class SurveysFormSchemaValidator extends FormSchemaValidator {
  private flattenElementsCache?: FlattenElementsCache;

  validate(): string[] {
    const result = super.validate();
    this.append(result, this.totalSection());
    this.append(result, this.totalInputCount());
    this.append(result, this.totalAttachFileSize());
    return result;
  }

  validateDeep(): string[] {
    const result = super.validateDeep();
    return result;
  }

  assertion(): void {
    super.assertion();
    this.assertError([
      this.totalSection(),
      this.totalInputCount(),
      this.totalAttachFileSize(),
    ]);
  }

  checkPublishable() {
    const errors = this.validate();
    if (errors.length > 0) {
      return false;
    }
    const schema = this.schema;
    return (
      schema.root.length > 0 &&
      !schema.root.find(
        (s) => s.children.filter((elem) => !isStaticElement(elem)).length === 0,
      )
    );
  }

  totalSection() {
    return this.schema.root.length > ValidationConstant.TOTAL_MAX_SECTION_COUNT
      ? `作成できるセクションは合計${ValidationConstant.TOTAL_MAX_SECTION_COUNT}までです`
      : undefined;
  }

  totalInputCount() {
    return this.getFlattenElements().length >
      ValidationConstant.TOTAL_MAX_INPUT_ELEMENTS
      ? `入力要素の合計数は${ValidationConstant.TOTAL_MAX_INPUT_ELEMENTS}までです`
      : undefined;
  }

  /**
   * トータルの添付ファイルの上限サイズの合計が200MB以下であること
   */
  totalAttachFileSize() {
    const fileInputs: IFileInputElement[] = this.getFlattenElements().filter(
      (e): e is IFileInputElement & RepeatableMetaInfo =>
        isInputElement(e) && isFileInputElement(e),
    );
    const totalFileSize = fileInputs.reduce(
      (previousValue, currentValue: IFileInputElement) =>
        previousValue + currentValue.maxSize,
      0,
    );
    return totalFileSize > ValidationConstant.TOTAL_MAX_FILE_SIZE
      ? `添付ファイルの最大サイズの合計値は${ValidationConstant.TOTAL_MAX_FILE_SIZE}MB以下にする必要があります`
      : undefined;
  }

  private get flattener() {
    return new FormSchemaConverter(this.schema);
  }

  private getFlattenElements(): FlattenElementsCache {
    if (this.flattenElementsCache) {
      return this.flattenElementsCache;
    }

    const flattenElements = this.flattener.expand();
    this.flattenElementsCache = flattenElements;
    return flattenElements;
  }
}
